import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
	Form, FormControl, FormField, FormItem, FormLabel,
	Input,
} from '@rentflowdev/ui'
import { EditableSectionHeader } from './editable-section-header'
import type { LandlordValues } from './types'

const landlordSchema = z.object({
	bankAccountId: z.string(),
	signatoryId:   z.string(),
	fullName:      z.string(),
	inn:           z.string(),
	organization:  z.string(),
	ogrn:          z.string(),
	legalAddress:  z.string(),
})

type LandlordFormSchema = z.infer<typeof landlordSchema>

interface StepLandlordProps {
	initial: LandlordValues
	formId:  string
	onValid: (values: LandlordValues) => void
}

export function StepLandlord({ initial, formId, onValid }: StepLandlordProps) {
	const { t } = useTranslation()

	// Section is read-only by default — values arrive auto-filled from the user's
	// CompanyProfile (resolved in the wizard's `buildInitialState`). Pencil
	// toggles per-contract overrides without writing back to the source.
	//
	// Note on `bankAccountId` / `signatoryId`: until backend ships dedicated
	// `AccountBankAccount[]` / `AccountSignatory[]` collections, these fields
	// hold freeform strings (formatted bank label, signatory full name) rather
	// than real FKs. We render them as plain Inputs so the user can type a
	// one-off override even when CompanyProfile is empty.
	const [editable, setEditable] = useState(false)

	const form = useForm<LandlordFormSchema>({
		resolver: zodResolver(landlordSchema),
		defaultValues: initial,
		mode: 'onChange',
	})

	// Profile loads async — reset form when initial values arrive from wizard state
	useEffect(() => {
		form.reset(initial)
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initial])

	function handleSubmit(values: LandlordFormSchema) {
		onValid(values)
	}

	return (
		<Form {...form}>
			<form
				id={formId}
				onSubmit={form.handleSubmit(handleSubmit)}
				className="flex flex-col gap-4"
			>
				<EditableSectionHeader
					title={t('deals.documents.wizard.landlord.title')}
					editable={editable}
					onToggle={() => setEditable((v) => !v)}
					editLabel={t('deals.documents.wizard.editSection')}
				/>

				<div className="flex gap-2">
					<FormField
						control={form.control}
						name="bankAccountId"
						render={({ field }) => (
							<FormItem className="flex-1">
								<FormLabel>{t('deals.documents.wizard.landlord.bankAccount')}</FormLabel>
								<FormControl>
									<Input
										{...field}
										disabled={!editable}
										placeholder={t('deals.documents.wizard.landlord.bankAccountPlaceholder')}
									/>
								</FormControl>
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="signatoryId"
						render={({ field }) => (
							<FormItem className="flex-1">
								<FormLabel>{t('deals.documents.wizard.landlord.signatory')}</FormLabel>
								<FormControl>
									<Input
										{...field}
										disabled={!editable}
										placeholder={t('deals.documents.wizard.landlord.signatoryPlaceholder')}
									/>
								</FormControl>
							</FormItem>
						)}
					/>
				</div>

				<div className="flex gap-2">
					<FormField
						control={form.control}
						name="fullName"
						render={({ field }) => (
							<FormItem className="flex-1">
								<FormLabel>{t('deals.documents.wizard.landlord.fullName')}</FormLabel>
								<FormControl>
									<Input
										{...field}
										disabled={!editable}
										placeholder={t('deals.documents.wizard.landlord.fullNamePlaceholder')}
									/>
								</FormControl>
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="inn"
						render={({ field }) => (
							<FormItem className="flex-1">
								<FormLabel>{t('deals.documents.wizard.landlord.inn')}</FormLabel>
								<FormControl>
									<Input
										{...field}
										disabled={!editable}
										placeholder={t('deals.documents.wizard.landlord.innPlaceholder')}
									/>
								</FormControl>
							</FormItem>
						)}
					/>
				</div>

				<div className="flex gap-2">
					<FormField
						control={form.control}
						name="organization"
						render={({ field }) => (
							<FormItem className="flex-1">
								<FormLabel>{t('deals.documents.wizard.landlord.organization')}</FormLabel>
								<FormControl>
									<Input
										{...field}
										disabled={!editable}
										placeholder={t('deals.documents.wizard.landlord.organizationPlaceholder')}
									/>
								</FormControl>
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="ogrn"
						render={({ field }) => (
							<FormItem className="flex-1">
								<FormLabel>{t('deals.documents.wizard.landlord.ogrn')}</FormLabel>
								<FormControl>
									<Input
										{...field}
										disabled={!editable}
										placeholder={t('deals.documents.wizard.landlord.ogrnPlaceholder')}
									/>
								</FormControl>
							</FormItem>
						)}
					/>
				</div>

				<FormField
					control={form.control}
					name="legalAddress"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('deals.documents.wizard.landlord.legalAddress')}</FormLabel>
							<FormControl>
								<Input
									{...field}
									disabled={!editable}
									placeholder={t('deals.documents.wizard.landlord.legalAddressPlaceholder')}
								/>
							</FormControl>
						</FormItem>
					)}
				/>
			</form>
		</Form>
	)
}
